import { computed, onMounted, ref, watch, type ComputedRef, type Ref } from 'vue';

/** Rows per page of every dictionary table. */
export const DICTIONARY_PAGE_SIZE = 50;

export function tablePage(state: { pagination: { offset: number; limit: number } }): number {
  const limit = state.pagination.limit || DICTIONARY_PAGE_SIZE;
  return Math.floor(state.pagination.offset / limit) + 1;
}

/**
 * `NxTable` keeps its pager offset internally. There is no page prop or
 * v-model. `setTableState({ pagination })` is the exposed write, and it does
 * not call `onTableStateChange`, so resetting the page (search, language) can
 * put the pager back on page 1 without asking for another page.
 */
export interface DictionaryTablePage {
  setTableState: (state: { pagination: { offset: number; limit: number } }) => void;
}

export function syncDictionaryTablePage(table: Ref<DictionaryTablePage | null>, page: () => number): void {
  function push(): void {
    const limit = DICTIONARY_PAGE_SIZE;
    table.value?.setTableState({
      pagination: { offset: Math.max(0, page() - 1) * limit, limit },
    });
  }
  watch(page, push);
  onMounted(push);
}

/**
 * The page of a list the table shows: `NxTable` is told which rows to show
 * (`data`) and how many there are (`total-count`) and reports the page the
 * reader asks for (`on-table-state-change`) — the list itself is in memory,
 * it comes from the config document.
 */
export function usePagedRows<T>(rows: () => readonly T[]): {
  table: Ref<DictionaryTablePage | null>;
  page: Ref<number>;
  pageRows: ComputedRef<T[]>;
  total: ComputedRef<number>;
  onState: (state: { pagination: { offset: number; limit: number } }) => Promise<void>;
  /** Back to the first page (the search or the language changed). */
  reset: () => void;
} {
  const page = ref(1);
  const table = ref<DictionaryTablePage | null>(null);
  syncDictionaryTablePage(table, () => page.value);
  const total = computed(() => rows().length);
  // A page that no longer exists (rows were removed) falls back to the last one.
  watch(total, (count) => {
    const last = Math.max(1, Math.ceil(count / DICTIONARY_PAGE_SIZE));
    if (page.value > last) page.value = last;
  });
  return {
    table,
    page,
    pageRows: computed(() => rows().slice((page.value - 1) * DICTIONARY_PAGE_SIZE, page.value * DICTIONARY_PAGE_SIZE)),
    total,
    onState(state) {
      page.value = tablePage(state);
      return Promise.resolve();
    },
    reset() {
      page.value = 1;
    },
  };
}
